import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns';
import { memo, useEffect, useState } from 'react';

import { EMPTY_HISTORY, useSelectionHistoryStore } from './selection-history-store.js';

import type { SelectionHistoryEntry } from './selection-history-store.js';

export type HistoryNavigatorProps = {
	revisionId: string;
	/** Called when the user clicks an entry or presses the back/forward button. */
	onNavigateTo: (entry: SelectionHistoryEntry, index: number) => void;
};

/**
 * A pair of back/forward navigation buttons that track inspector selection history.
 * Right-clicking either button opens a full history menu.
 */
export const HistoryNavigator = memo(function HistoryNavigator({ revisionId, onNavigateTo }: HistoryNavigatorProps) {
	const { entries, currentIndex } = useSelectionHistoryStore((state) => state.histories[revisionId] ?? EMPTY_HISTORY);

	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

	const canGoBack = currentIndex > 0;
	const canGoForward = currentIndex < entries.length - 1;

	const closeMenu = () => setMenuAnchor(null);

	const handleBackClick = () => {
		if (currentIndex > 0) {
			onNavigateTo(entries[currentIndex - 1]!, currentIndex - 1);
		}
	};

	const handleForwardClick = () => {
		if (currentIndex < entries.length - 1) {
			onNavigateTo(entries[currentIndex + 1]!, currentIndex + 1);
		}
	};

	// Keyboard shortcuts: Ctrl+[ (back) and Ctrl+] (forward) on both Mac and Windows.
	// Re-register on every change to the values the handler reads so it never closes over stale
	// history state — re-binding a keydown listener is cheap.
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!e.ctrlKey) {
				return;
			}
			// Match on physical key position (e.code) rather than the produced
			// character (e.key) so the shortcut works regardless of keyboard layout.
			if (e.code === 'BracketLeft' && currentIndex > 0) {
				e.preventDefault();
				onNavigateTo(entries[currentIndex - 1]!, currentIndex - 1);
			} else if (e.code === 'BracketRight' && currentIndex < entries.length - 1) {
				e.preventDefault();
				onNavigateTo(entries[currentIndex + 1]!, currentIndex + 1);
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [currentIndex, entries, onNavigateTo]);

	const handleButtonContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		setMenuAnchor(e.currentTarget);
	};

	const handleMenuItemClick = (entry: SelectionHistoryEntry, index: number) => {
		closeMenu();
		onNavigateTo(entry, index);
	};

	return (
		<div className='flex h-[calc(1.4375em+0.5rem+2px)] shrink-0 grow-0 items-center gap-4 rounded-lg border border-gray-a6 bg-gray-2/60 px-2.5 backdrop-blur-md'>
			<Tooltip
				title={
					<span>
						Back (Ctrl+[)
						<br />
						Right-click to see full history
					</span>
				}
				disableInteractive
			>
				<IconButton
					disabled={!canGoBack}
					onClick={handleBackClick}
					onContextMenu={handleButtonContextMenu}
					className='-mx-1 text-gray-11 hover:text-gray-12 disabled:opacity-40'
				>
					<NavigateBefore />
				</IconButton>
			</Tooltip>

			<Tooltip
				title={
					<span>
						Forward (Ctrl+])
						<br />
						Right-click to see full history
					</span>
				}
				disableInteractive
			>
				<IconButton
					disabled={!canGoForward}
					onClick={handleForwardClick}
					onContextMenu={handleButtonContextMenu}
					className='-mx-1 text-gray-11 hover:text-gray-12 disabled:opacity-40'
				>
					<NavigateNext />
				</IconButton>
			</Tooltip>

			<Menu
				anchorEl={menuAnchor}
				open={menuAnchor !== null}
				onClose={closeMenu}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				{entries.toReversed().map((entry, index) => {
					const originalIndex = entries.length - 1 - index;
					return (
						<MenuItem
							key={entry.id}
							selected={originalIndex === currentIndex}
							onClick={() => handleMenuItemClick(entry, originalIndex)}
							className={clsx(originalIndex === currentIndex && 'font-semibold')}
						>
							<div className='flex w-full items-center justify-between gap-1'>
								<span>
									<span className='mr-1 text-gray-a9'>{originalIndex + 1}. </span>
									{entry.label}
								</span>

								<span className='text-gray-a9'>{formatAge(entry.timestamp)}</span>
							</div>
						</MenuItem>
					);
				})}
			</Menu>
		</div>
	);
});

/**
 * Formats a timestamp as a short relative age string with one unit, rounded down.
 * @param timestamp - Unix ms timestamp to compare against now.
 * @returns E.g. "just now", "5 min", "3 hr", "2 days".
 */
function formatAge(timestamp: number): string {
	const now = new Date();
	const then = new Date(timestamp);
	const hours = differenceInHours(now, then);
	if (hours >= 24) {
		return `${Math.floor(hours / 24)} days`;
	}
	if (hours >= 1) {
		return `${hours} hr`;
	}
	const minutes = differenceInMinutes(now, then);
	if (minutes >= 1) {
		return `${minutes} min`;
	}
	const seconds = differenceInSeconds(now, then);
	if (seconds >= 5) {
		return `${seconds} sec`;
	}
	return 'just now';
}
